import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/env';
import {
  IDLE_TIMEOUT_MS,
  WARNING_DURATION_SEC,
  TAB_HEARTBEAT_MS,
} from '../../constants/session';
import { useAuth } from '../../context/AuthContext';
import SessionTimeoutDialog from '../admin/SessionTimeoutDialog';
import { getAuthHeaders, getAuthToken, isTokenExpired } from '../../utils/auth';
import {
  getLastActivity,
  getWarningExpiresAt,
  registerTab,
  setWarningExpiresAt,
  touchTab,
  updateLastActivity,
} from '../../services/tabSession';
import {
  broadcastSessionEvent,
  SESSION_EVENTS,
  subscribeSessionEvents,
} from '../../services/sessionSync';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'touchmove',
  'wheel',
];

/**
 * Lightweight ping to confirm the token is still accepted by the API.
 * Returns true only when the server accepts the current session token.
 */
async function pingSession() {
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) return false;

  try {
    const response = await axios.get(`${API_BASE_URL}api/v1/categories`, {
      headers: getAuthHeaders(),
      timeout: 8000,
      validateStatus: (status) => status >= 200 && status < 300,
    });
    return Boolean(response);
  } catch {
    // Auth rejection, network failure, or timeout — treat as invalid session
    return false;
  }
}

export default function SessionManager() {
  const { logout } = useAuth();
  const [warningOpen, setWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION_SEC);
  const [continuing, setContinuing] = useState(false);

  const warningOpenRef = useRef(false);
  const lastActivityPingRef = useRef(0);

  const syncCountdownFromStorage = useCallback(() => {
    const expiresAt = getWarningExpiresAt();
    if (!expiresAt) return WARNING_DURATION_SEC;
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
  }, []);

  const closeWarning = useCallback(() => {
    warningOpenRef.current = false;
    setWarningOpen(false);
    setWarningExpiresAt(null);
    setCountdown(WARNING_DURATION_SEC);
  }, []);

  const recordActivity = useCallback((broadcast = true) => {
    // While the warning is visible, ignore all background activity.
    // The modal must only close via Continue Session or Logout.
    if (warningOpenRef.current) return;

    const now = Date.now();
    updateLastActivity(now);

    if (broadcast && now - lastActivityPingRef.current > 5000) {
      lastActivityPingRef.current = now;
      broadcastSessionEvent(SESSION_EVENTS.ACTIVITY, { ts: now });
    }
  }, []);

  const openWarning = useCallback(
    (broadcast = true) => {
      if (warningOpenRef.current) return;

      const expiresAt = Date.now() + WARNING_DURATION_SEC * 1000;
      setWarningExpiresAt(expiresAt);
      warningOpenRef.current = true;
      setWarningOpen(true);
      setCountdown(WARNING_DURATION_SEC);

      if (broadcast) {
        broadcastSessionEvent(SESSION_EVENTS.WARNING, { expiresAt });
      }
    },
    []
  );

  const handleContinueSession = useCallback(async () => {
    setContinuing(true);
    try {
      const sessionValid = await pingSession();
      if (!sessionValid) {
        closeWarning();
        logout({
          reason: 'idle',
          message: 'Your session is no longer valid. Please log in again.',
          broadcast: true,
        });
        return;
      }

      updateLastActivity(Date.now());
      broadcastSessionEvent(SESSION_EVENTS.CONTINUE);
      closeWarning();
    } finally {
      setContinuing(false);
    }
  }, [closeWarning, logout]);

  const handleLogout = useCallback(() => {
    closeWarning();
    logout({ reason: 'manual', message: 'You have been signed out.', broadcast: true });
  }, [closeWarning, logout]);

  const handleIdleExpiry = useCallback(() => {
    closeWarning();
    logout({
      reason: 'idle',
      message: 'Your session has expired due to inactivity. Please log in again.',
      broadcast: true,
    });
  }, [closeWarning, logout]);

  useEffect(() => {
    registerTab();

    const handleActivity = () => recordActivity(true);
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    const heartbeatId = window.setInterval(() => {
      touchTab();
    }, TAB_HEARTBEAT_MS);

    const idleCheckId = window.setInterval(() => {
      const expiresAt = getWarningExpiresAt();

      if (warningOpenRef.current && expiresAt) {
        const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
        setCountdown(Math.max(remaining, 0));
        if (remaining <= 0) {
          handleIdleExpiry();
        }
        return;
      }

      const idleFor = Date.now() - getLastActivity();
      if (idleFor >= IDLE_TIMEOUT_MS) {
        openWarning(true);
      }
    }, 1000);

    const unsubscribe = subscribeSessionEvents(({ type, payload }) => {
      switch (type) {
        case SESSION_EVENTS.ACTIVITY:
          if (payload?.ts) {
            updateLastActivity(payload.ts);
          }
          break;
        case SESSION_EVENTS.WARNING:
          if (payload?.expiresAt) {
            setWarningExpiresAt(payload.expiresAt);
          }
          warningOpenRef.current = true;
          setWarningOpen(true);
          setCountdown(syncCountdownFromStorage());
          break;
        case SESSION_EVENTS.CONTINUE:
          closeWarning();
          updateLastActivity(Date.now());
          break;
        case SESSION_EVENTS.LOGOUT:
          logout({
            reason: payload?.reason || 'manual',
            message: payload?.message,
            broadcast: false,
            redirect: true,
          });
          break;
        default:
          break;
      }
    });

    const storageListener = (event) => {
      if (event.key === 'rfis_session_warning_expires') {
        if (event.newValue) {
          warningOpenRef.current = true;
          setWarningOpen(true);
          setCountdown(syncCountdownFromStorage());
        } else if (!event.newValue) {
          closeWarning();
        }
      }
    };

    window.addEventListener('storage', storageListener);

    updateLastActivity();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      window.clearInterval(heartbeatId);
      window.clearInterval(idleCheckId);
      window.removeEventListener('storage', storageListener);
      unsubscribe();
    };
  }, [
    closeWarning,
    handleIdleExpiry,
    logout,
    openWarning,
    recordActivity,
    syncCountdownFromStorage,
  ]);

  return (
    <SessionTimeoutDialog
      open={warningOpen}
      countdown={countdown}
      onContinue={handleContinueSession}
      onLogout={handleLogout}
      loading={continuing}
    />
  );
}
